import { OAuth2Config, OAuth2Token } from '../types';

import { AuthorizationUrlStrategy } from '../strategies/authorization-url.strategy';
import { TokenExchangeStrategy } from '../strategies/token-exchange.strategy';
import { UserProfile } from './interfaces/profile-fetcher.interface';
import { BaseProfileFetcher } from '../profile/base-profile-fetcher';

export abstract class OAuth2Provider {
  protected authUrlStrategy: AuthorizationUrlStrategy;
  protected tokenStrategy: TokenExchangeStrategy;
  protected profileFetcher?: BaseProfileFetcher;

  constructor(
    protected config: OAuth2Config,
    authUrlStrategy?: AuthorizationUrlStrategy,
    tokenStrategy?: TokenExchangeStrategy,
    profileFetcher?: BaseProfileFetcher,
  ) {
    this.authUrlStrategy = authUrlStrategy || this.createAuthorizationUrlStrategy();
    this.tokenStrategy = tokenStrategy || this.createTokenExchangeStrategy();
    this.profileFetcher = profileFetcher;
  }

  // Factory methods
  protected abstract createAuthorizationUrlStrategy(): AuthorizationUrlStrategy;
  protected abstract createTokenExchangeStrategy(): TokenExchangeStrategy;

  // Profile fetching methods
  async fetchProfile(accessToken: string): Promise<UserProfile> {
    if (!this.profileFetcher) {
      throw new Error('Profile fetcher not configured for this provider');
    }
    return this.profileFetcher.fetchUserInfo(accessToken);
  }

  getProfileEndpoint(): string {
    if (!this.profileFetcher) {
      throw new Error('Profile fetcher not configured for this provider');
    }
    return this.profileFetcher.getEndpoint();
  }

  setProfileFetcher(profileFetcher: BaseProfileFetcher): void {
    this.profileFetcher = profileFetcher;
  }

  hasProfileFetcher(): boolean {
    return !!this.profileFetcher;
  }

  generateAuthorizationUrl(state: string, codeChallenge?: string): string {
    return this.authUrlStrategy.generateAuthorizationUrl(this.config, state, codeChallenge);
  }

  async exchangeCodeForToken(code: string, codeVerifier?: string): Promise<OAuth2Token> {
    return this.tokenStrategy.exchangeCodeForToken(code, this.config, codeVerifier);
  }

  async refreshToken(refreshToken: string): Promise<OAuth2Token> {
    return this.tokenStrategy.refreshToken(refreshToken, this.config);
  }
}
